import {baseRequest} from 'src/utils/redux-api-v2';


type StartPollerOptions = {
  interval?: number;
};

let pollerTimer: number | null = null;
let isPolling = false;
let removeListeners: (() => void) | null | undefined = null;

const SYNC_KEY = 'untranslatedLabels';
const SYNC_STATE_KEY = '__lastSyncedValue__';

function isOnline(): boolean {
  try {
    return typeof navigator !== 'undefined' && navigator.onLine;
  } catch (_: any) {
    return false;
  }
}

function localStorageAvailable(): boolean {
  try {
    const testKey = '__test_localstorage__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch (_: any) {
    return false;
  }
}

function getStoredLabels():
  | {
      [key: string]: string;
    }
  | null
  | undefined {
  try {
    const raw = localStorage.getItem(SYNC_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (
      parsed &&
      typeof parsed === 'object' &&
      Object.keys(parsed).length > 0
    ) {
      return parsed;
    }
  } catch (_: any) {
    try {
      localStorage.removeItem(SYNC_KEY);
    } catch (_: any) {}
  }
  return null;
}

function pollOnce(store: any): void {
  if (isPolling || !isOnline() || !localStorageAvailable()) {
    return;
  }

  const data = getStoredLabels();
  if (!data) {
    return;
  }

  let newSnapshot = '';
  try {
    newSnapshot = JSON.stringify(data);
    const last = localStorage.getItem(SYNC_STATE_KEY);
    if (last === newSnapshot) {
      return;
    }
  } catch (_: any) {
    // skip snapshot logic
  }

  isPolling = true;

  const trigger = () => {
    store
      .dispatch(
        baseRequest('api/dynamic-translation', {
          method: 'POST',
          // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'data' does not exist in type 'RequestInit'.
          data,
          query: {},
          options: {
            apiPath: '/trmapi/',
          },
        }),
      )
      .then(() => {
        try {
          localStorage.removeItem(SYNC_KEY);
          if (newSnapshot) {
            localStorage.setItem(SYNC_STATE_KEY, newSnapshot);
          }
        } catch (_: any) {
          // fail silently
        }
      })
      .catch(() => {
        // silently fail
      })
      .finally(() => {
        isPolling = false;
        localStorage.removeItem(SYNC_KEY);
        if (newSnapshot) {
          localStorage.setItem(SYNC_STATE_KEY, newSnapshot);
        }
      });
  };

  try {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(trigger);
    } else {
      setTimeout(trigger, 0);
    }
  } catch (_: any) {
    // fallback to immediate
    setTimeout(trigger, 0);
  }
}

function setupLifecycleListeners(interval: number, store: any): () => void {
  const onVisibility = () => {
    try {
      if (document.visibilityState === 'visible') {
        pollOnce(store);
        resetInterval(interval, store);
      }
    } catch (_: any) {}
  };

  const onReconnect = () => {
    if (isOnline()) {
      pollOnce(store);
      resetInterval(interval, store);
    }
  };

  const onUnload = () => {
    stopUntranslatedLabelsPoller();
  };

  try {
    window.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onReconnect);
    window.addEventListener('beforeunload', onUnload);
  } catch (_: any) {}

  return () => {
    try {
      window.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onReconnect);
      window.removeEventListener('beforeunload', onUnload);
    } catch (_: any) {}
  };
}

function resetInterval(interval: number, store: any) {
  try {
    if (pollerTimer) {
      clearInterval(pollerTimer);
    }
    // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
    pollerTimer = setInterval(() => pollOnce(store), interval);
  } catch (_: any) {}
}

export function startUntranslatedLabelsPoller(
  store: any,
  {interval = 120000}: StartPollerOptions = {},
): void {
  try {
    if (!localStorageAvailable()) {
      return;
    }
    stopUntranslatedLabelsPoller(); // avoid duplicate timers
    pollOnce(store); // immediate trigger
    resetInterval(interval, store);
    removeListeners = setupLifecycleListeners(interval, store);
  } catch (_: any) {}
}

export function stopUntranslatedLabelsPoller(): void {
  try {
    if (pollerTimer) {
      clearInterval(pollerTimer);
    }
    pollerTimer = null;

    if (removeListeners) {
      removeListeners();
      removeListeners = null;
    }

    isPolling = false;
  } catch (_: any) {}
}
